import React from "react";
import { Box, Stack, Text } from "@chakra-ui/react";
import {
  HiOutlineCalendar,
  HiOutlineHome,
  HiOutlineMail,
  HiOutlinePhone,
} from "react-icons/hi";
import {
  ClientProfileTypeMarker,
  InfoLine,
  SectionEyebrow,
} from "../components/ClientProfileType";
import { ClientProfileRenderer } from "../registry.types";

const HOMEOWNER_ACCENT = "#2F6FED";

const formatScheduledDate = (value: Date | string) => {
  const date = new Date(value);
  return date.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const homeownerName = (homeowner?: { firstName?: string; lastName?: string } | null) => {
  if (!homeowner) {
    return "Unnamed homeowner";
  }
  const parts = [homeowner.firstName, homeowner.lastName].filter(Boolean);
  return parts.length ? parts.join(" ") : "Unnamed homeowner";
};

export const homeownerRenderer: ClientProfileRenderer<"HOMEOWNER"> = {
  badge: { label: "Homeowner", colorScheme: "blue" },
  rowDisplayName: order => homeownerName(order.clientProfile.homeowner),
  RowSummary: ({ order }) => (
    <Stack spacing="2px" lineHeight="short">
      <Text textTransform="capitalize" fontWeight="medium" color="gray.800">
        {homeownerName(order.clientProfile.homeowner)}
      </Text>
      <ClientProfileTypeMarker
        accent={HOMEOWNER_ACCENT}
        icon={HiOutlineHome}
        label="Homeowner"
      />
    </Stack>
  ),
  DetailClientCard: ({ order }) => {
    const homeowner = order.clientProfile.homeowner;
    return (
      <Box
        borderLeft="3px solid"
        borderLeftColor={HOMEOWNER_ACCENT}
        pl="14px"
      >
        <SectionEyebrow accent={HOMEOWNER_ACCENT} icon={HiOutlineHome} label="Homeowner" />
        <Text
          fontSize="lg"
          fontWeight="semibold"
          textTransform="capitalize"
          color="gray.900"
          mb="10px"
          lineHeight="short"
        >
          {homeownerName(homeowner)}
        </Text>
        <Stack spacing="6px">
          {homeowner?.email && <InfoLine icon={HiOutlineMail}>{homeowner.email}</InfoLine>}
          {homeowner?.phoneNumber && (
            <InfoLine icon={HiOutlinePhone}>{homeowner.phoneNumber}</InfoLine>
          )}
        </Stack>
      </Box>
    );
  },
  DetailAddressBillingCard: ({ order }) => {
    const schedule = order.locationSchedules?.[0];
    if (!schedule) {
      return null;
    }
    return (
      <Box>
        <SectionEyebrow icon={HiOutlineCalendar} label="Service schedule" />
        <Text fontSize="md" fontWeight="semibold" color="gray.900">
          {formatScheduledDate(schedule.scheduledDate)}
        </Text>
      </Box>
    );
  },
};
